import React from 'react';

import { ActionIcon, Badge, Box, Flex, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconPencil, IconTrash } from '@tabler/icons-react';
import { TYPE_COLORS } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/constants';
import { JsonProperty } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/properties/types';
import { useTranslation } from 'react-i18next';

interface ParameterRowProps {
  name: string;
  property: JsonProperty;
  fullPath: string[];
  isRequired: boolean;
  onEdit: (fullPath: string[]) => void;
  onDelete: (fullPath: string[]) => void;
  onToggleRequired?: (fullPath: string[], isRequired: boolean) => void;
  isExpanded?: boolean;
  onToggleExpanded?: (fullPath: string[]) => void;
  isDeletable?: boolean; // Allow hiding delete button for special cases like array items
}

export function ParameterRow({
  name,
  property,
  fullPath,
  isRequired,
  onEdit,
  onDelete,
  onToggleRequired,
  isExpanded,
  onToggleExpanded,
  isDeletable = true
}: ParameterRowProps) {
  const { t } = useTranslation();

  const isExpandable = property.type === 'object' || property.type === 'array';

  return (
    <Box
      p="md"
      bg="white"
      sx={(theme) => ({
        border: `1px solid ${theme.other.gray.border}`,
        borderRadius: theme.defaultRadius,
        '&:hover': {
          boxShadow: theme.other.baseShadow
        }
      })}>
      <Flex justify="space-between" align="flex-start">
        <Box style={{ flex: 1 }}>
          <Flex align="center" gap="xs" mb="xs">
            {isExpandable && (
              <ActionIcon size="xs" variant="subtle" onClick={() => onToggleExpanded?.(fullPath)} c="dimmed">
                {isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
              </ActionIcon>
            )}
            <Text fw={500} size="sm">
              {name}
            </Text>
            <Badge size="sm" variant="light" tt="none" fw={500} color={TYPE_COLORS[property.type] || 'gray'}>
              {property.type}
            </Badge>
          </Flex>

          {property.description && (
            <Text size="sm" c="dimmed" mb="xs">
              {property.description}
            </Text>
          )}

          {property.type === 'string' && property.enum?.length && (
            <Flex align="center" gap="xs" wrap="wrap">
              <Text size="xs" c="dimmed" fw={500}>
                {t('JSON_SCHEMA_BUILDER.VALUES')}:
              </Text>
              {property.enum.map((enumValue, index) => (
                <Text
                  key={index}
                  size="xs"
                  px="xs"
                  py={3}
                  bg="gray.1"
                  sx={(theme) => ({
                    borderRadius: theme.defaultRadius,
                    fontSize: theme.fontSizes.xs,
                    fontWeight: 400,
                    color: theme.other.text.dimmed3
                  })}>
                  {enumValue}
                </Text>
              ))}
            </Flex>
          )}
        </Box>

        <Flex gap="xs" align="center">
          {onToggleRequired && (
            <UnstyledButton
              onClick={() => onToggleRequired(fullPath, !isRequired)}
              px="xs"
              py={4}
              w={80}
              sx={(theme) => ({
                borderRadius: theme.defaultRadius,
                border: `1px solid ${theme.other.gray.border}`,
                backgroundColor: isRequired ? theme.colors.red[0] : theme.colors.gray[0],
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: isRequired ? theme.colors.red[1] : theme.colors.gray[1]
                }
              })}>
              <Text size="xs" fw={500} c={isRequired ? 'red.6' : 'dimmed'}>
                {isRequired ? t('JSON_SCHEMA_BUILDER.REQUIRED') : t('JSON_SCHEMA_BUILDER.OPTIONAL')}
              </Text>
            </UnstyledButton>
          )}

          <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => onEdit(fullPath)}>
            <IconPencil size={14} />
          </ActionIcon>

          {isDeletable && (
            <ActionIcon size="sm" variant="subtle" color="red" onClick={() => onDelete(fullPath)}>
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
